

import * as React from "react"
import { Upload, File, X, FileText, FileImage, FileIcon as FilePdf, Edit, Tag as TagIcon } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import type { Document } from "@/lib/types"
import { ConfirmationDialog } from "@/components/shared/confirmation-dialog"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { TagList } from "@/components/ui/tag-badge"
import { TagSelector } from "@/components/ui/tag-selector"
import { tagService, type Tag, type CreateTagPayload } from "@/services/TagService"
import { documentTagService } from "@/services/DocumentTagService"
import { toast } from '@/lib/toast'

interface DocumentsProps {
  onUploadClick: () => void
  /** Ir al formulario de siempre. Sin esto no se pinta: el camino por defecto
   *  es hablarlo, y el formulario es la salida de quien ya se sabe los campos
   *  (3.3, D2). */
  onFormClick?: () => void
  documents: Document[]
  loading: boolean
  error: string | null
  onDelete: (id: string) => Promise<void>
  onUpdate?: (
    id: string,
    data: Omit<Partial<Document>, 'file' | 'category' | 'subcategory'> & {
      category?: string | null
      subcategory?: string | null
      file?: File
    }
  ) => Promise<void>
  onTagsUpdate?: (documentId: string) => Promise<void>
  categories?: { id: string; name: string; subcategories: { id: string; name: string }[] }[]
  translations?: Record<string, unknown>
  onRefreshCategories?: () => Promise<void>
}

export function Documents({ onUploadClick, onFormClick, documents, loading, error, onDelete, onUpdate, onTagsUpdate, categories, translations, onRefreshCategories }: DocumentsProps) {
  // Translation helper function
  const t = (key: string): string => {
    if (!translations) return key
    const result = key.split('.').reduce((obj: Record<string, unknown>, k: string) => obj?.[k] as Record<string, unknown>, translations as Record<string, unknown>)
    return typeof result === 'string' ? result : key
  }
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false)
  const [deletingId, setDeletingId] = React.useState<string | null>(null)
  const [itemToDelete, setItemToDelete] = React.useState<Document | null>(null)
  
  // Edit state
  const [editDialogOpen, setEditDialogOpen] = React.useState(false)
  const [editingDocument, setEditingDocument] = React.useState<Document | null>(null)
  const [editForm, setEditForm] = React.useState({
    title: '',
    description: '',
    category: '',
    subcategory: ''
  })
  const [editFile, setEditFile] = React.useState<File | null>(null)
  const [updatingId, setUpdatingId] = React.useState<string | null>(null)

  // Tag management state
  const [tagDialogOpen, setTagDialogOpen] = React.useState(false)
  const [tagEditingDocument, setTagEditingDocument] = React.useState<Document | null>(null)
  const [availableTags, setAvailableTags] = React.useState<Tag[]>([])
  const [selectedTags, setSelectedTags] = React.useState<Tag[]>([])
  const [tagsLoading, setTagsLoading] = React.useState(false)
  const [updatingTags, setUpdatingTags] = React.useState(false)

  const handleDeleteClick = (document: Document) => {
    setItemToDelete(document)
    setDeleteDialogOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return
    
    setDeletingId(itemToDelete.id)
    try {
      await onDelete(itemToDelete.id)
      setDeleteDialogOpen(false)
      setItemToDelete(null)
    } catch (error) {
      console.error('Error deleting document:', error)
    } finally {
      setDeletingId(null)
    }
  }

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false)
    setItemToDelete(null)
    setDeletingId(null)
  }

  const handleEditClick = async (document: Document) => {
    setEditingDocument(document)
    
    // Refresh categories before opening the dialog
    if (onRefreshCategories) {
      try {
        await onRefreshCategories()
      } catch (error) {
        console.warn('Failed to refresh categories:', error)
      }
    }
    
    // Find category and subcategory IDs based on names
    const categoryObj = categories?.find(cat => cat.name === document.category)
    const subcategoryObj = categoryObj?.subcategories.find(sub => sub.name === document.subcategory)
    
    setEditForm({
      title: document.title,
      description: document.description || '',
      category: categoryObj?.id || '',
      subcategory: subcategoryObj?.id || ''
    })
    setEditFile(null)
    setEditDialogOpen(true)
  }

  const handleEditSubmit = async () => {
    if (!editingDocument || !onUpdate) return
    
    setUpdatingId(editingDocument.id)
    try {
      await onUpdate(editingDocument.id, {
        title: editForm.title,
        description: editForm.description,
        category: editForm.category,
        subcategory: editForm.subcategory,
        file: editFile || undefined,
      })
      setEditDialogOpen(false)
      setEditingDocument(null)
      setEditFile(null)
    } catch (error) {
      console.error('Error updating document:', error)
    } finally {
      setUpdatingId(null)
    }
  }

  const handleCancelEdit = () => {
    setEditDialogOpen(false)
    setEditingDocument(null)
    setEditFile(null)
    setUpdatingId(null)
  }

  // Tag management functions
  const handleTagsClick = async (document: Document) => {
    setTagEditingDocument(document)
    setTagsLoading(true)
    setTagDialogOpen(true)

    try {
      // Load available tags (document type)
      const availableTagsResponse = await tagService.getTagsByType('document')
      setAvailableTags(availableTagsResponse)

      // Build a map to hydrate colors for existing tags
      const colorById = new Map(availableTagsResponse.map(t => [t.id, t.color]))

      // Convert document tags to Tag format for the selector with proper colors
      const documentTags: Tag[] = (document.tags || []).map(docTag => ({
        id: docTag.tag_id,
        name: docTag.tag_name,
        color: colorById.get(docTag.tag_id) || "#1238c4",
        tag_type: docTag.tag_type as 'user' | 'conversation' | 'system' | 'document' | 'property'
      }))

      setSelectedTags(documentTags)
    } catch (error) {
      console.error('Error loading tags:', error)
    } finally {
      setTagsLoading(false)
    }
  }

  const handleTagsSubmit = async () => {
    if (!tagEditingDocument) return

    setUpdatingTags(true)
    try {
      // Get current document tags
      const currentTagIds = tagEditingDocument.tags?.map(tag => tag.tag_id) || []
      const newTagIds = selectedTags.map(tag => tag.id)

      // Find tags to add and remove
      const tagsToAdd = newTagIds.filter(id => !currentTagIds.includes(id))
      const tagsToRemove = currentTagIds.filter(id => !newTagIds.includes(id))

      // Remove tags first
      if (tagsToRemove.length > 0) {
        await documentTagService.removeTagsFromDocument(tagEditingDocument.id, tagsToRemove)
      }

      // Add new tags
      if (tagsToAdd.length > 0) {
        const tagsToAssign = selectedTags
          .filter(tag => tagsToAdd.includes(tag.id))
          .map(tag => documentTagService.tagToAssignment(tag))
        
        await documentTagService.assignTagsToDocument(tagEditingDocument.id, tagsToAssign)
      }

      // Refresh document data
      if (onTagsUpdate) {
        await onTagsUpdate(tagEditingDocument.id)
      }

      setTagDialogOpen(false)
      setTagEditingDocument(null)
    } catch (error) {
      console.error('Error updating document tags:', error)
      toast.error('Failed to update document tags')
    } finally {
      setUpdatingTags(false)
    }
  }

  const handleCancelTags = () => {
    setTagDialogOpen(false)
    setTagEditingDocument(null)
    setSelectedTags([])
  }

  const handleCreateTag = async (tagData: { name: string; color: string }): Promise<Tag> => {
    try {
      const payload: CreateTagPayload = {
        name: tagData.name,
        color: tagData.color,
        tag_type: 'document',
        description: undefined
      }
      const newTag = await tagService.createTag(payload)
      setAvailableTags(prev => [...prev, newTag])
      return newTag
    } catch (error) {
      console.error('Error creating tag:', error)
      throw error
    }
  }

  const getFileIcon = (type: string) => {
    switch (type) {
      case "pdf":
        return <FilePdf className="h-4 w-4 text-destructive" />
      case "doc":
      case "docx":
        return <FileText className="h-4 w-4 text-primary" />
      case "jpg":
      case "png":
      case "gif":
        return <FileImage className="h-4 w-4 text-success" />
      default:
        return <File className="h-4 w-4" />
    }
  }
  
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('knowledge.filters.documents')}</CardTitle>
          <CardDescription>{t('knowledge.documents_description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
            <CardTitle>{t('knowledge.filters.documents')}</CardTitle>
        </CardHeader>
        <CardContent>
            <p className="text-destructive">Error loading documents: {error}</p>
        </CardContent>
      </Card>
    )
  }


  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{t('knowledge.filters.documents')}</CardTitle>
          <CardDescription>{t('knowledge.documents_description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1">
            <Button className="w-full" onClick={onUploadClick}>
              <Upload className="mr-2 h-4 w-4" /> {t('knowledge.upload_document')}
            </Button>
            {/* **Debajo y en pequeno, no al lado** (D2). Dos botones iguales
                serian dos puertas al mismo sitio, y quien llega por primera vez
                tendria que elegir sin saber que elige. Asi hay un camino, y una
                salida para quien ya se sabe los campos. */}
            {onFormClick && (
              <Button variant="link" size="sm" className="w-full" onClick={onFormClick}>
                {t('knowledge.upload_form')}
              </Button>
            )}
          </div>

          <div className="space-y-2">
            {documents.map((doc) => (
              <div key={doc.id} className="flex flex-col space-y-2 rounded-lg border p-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-2">
                    {getFileIcon(doc.type)}
                    <div className="flex flex-col">
                      <span className="font-medium">{doc.title}</span>
                      <span className="text-sm text-muted-foreground">{doc.name}</span>
                      <p className="mt-1 text-sm text-muted-foreground">{doc.description}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="text-xs text-muted-foreground">{doc.size}</span>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleTagsClick(doc)}
                      className="text-primary hover:bg-primary/10 hover:text-primary"
                    >
                      <TagIcon className="mr-1 h-3 w-3" />
                      Tags ({doc.tag_count || 0})
                    </Button>
                    {onUpdate && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleEditClick(doc)}
                        disabled={updatingId === doc.id}
                        className="text-primary hover:bg-primary/10 hover:text-primary"
                      >
                        <Edit className="mr-1 h-3 w-3" />
                        Edit
                      </Button>
                    )}
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => handleDeleteClick(doc)}
                      disabled={deletingId === doc.id}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                    <span>{doc.category}</span>
                    <span>/</span>
                    <span>{doc.subcategory}</span>
                  </div>
                  {doc.tags && doc.tags.length > 0 && (
                    <TagList
                      tags={doc.tags.map(tag => ({
                        id: tag.tag_id,
                        name: tag.tag_name,
                        color: "#1238c4", // Default color
                        type: tag.tag_type
                      }))}
                      size="sm"
                      variant="outline"
                      maxTags={3}
                      className="mt-1"
                    />
                  )}
                </div>
              </div>
            ))}
             {documents.length === 0 && !loading && (
              <div className="py-4 text-center text-muted-foreground">
                No documents found.
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={t('knowledge.delete_confirmation.title')}
        message={itemToDelete ? (t('knowledge.delete_confirmation.document_message') as string).replace('{{title}}', itemToDelete.title) : ''}
        description={t('knowledge.delete_confirmation.description')}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        loading={deletingId !== null}
        translations={t}
      />

      {/* Edit Document Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5" />
              Edit Document
            </DialogTitle>
            <DialogDescription>
              Update the document information below.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Document Info Section */}
            {editingDocument && (
              <div className="grid gap-4 rounded-lg bg-muted/50 p-4">
                <h4 className="text-sm font-medium">Document Information</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Type:</span>
                    <span className="ml-2 font-medium">{editingDocument.type?.toUpperCase()}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Size:</span>
                    <span className="ml-2 font-medium">{editingDocument.size}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Uploaded:</span>
                    <span className="ml-2 font-medium">
                      {editingDocument.addedDate ? new Date(editingDocument.addedDate).toLocaleDateString() : 'Unknown'}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">File:</span>
                    <span className="ml-2 font-medium">{editingDocument.name}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Edit Form */}
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={editForm.title}
                  onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Document title"
                  className="font-medium"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={editForm.description}
                  onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Document description"
                  rows={4}
                />
              </div>
              {categories && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="category">Category *</Label>
                    <Select
                      value={editForm.category}
                      onValueChange={(value) => setEditForm(prev => ({ ...prev, category: value, subcategory: '' }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="subcategory">Subcategory *</Label>
                    <Select
                      value={editForm.subcategory}
                      onValueChange={(value) => setEditForm(prev => ({ ...prev, subcategory: value }))}
                      disabled={!editForm.category}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select subcategory" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories
                          .find(cat => cat.id === editForm.category)
                          ?.subcategories.map((sub) => (
                            <SelectItem key={sub.id} value={sub.id}>
                              {sub.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              <div className="grid gap-2">
                <Label htmlFor="replace-file">Replace file (optional)</Label>
                <Input
                  id="replace-file"
                  type="file"
                  accept=".pdf,.doc,.docx,.txt,.csv"
                  onChange={(e) => setEditFile(e.target.files?.[0] || null)}
                />
                {editFile && (
                  <p className="text-xs text-muted-foreground">
                    Selected: {editFile.name}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="flex justify-end space-x-2 border-t pt-4">
            <Button variant="outline" onClick={handleCancelEdit}>
              Cancel
            </Button>
            <Button 
              onClick={handleEditSubmit} 
              disabled={updatingId !== null || !editForm.title || !editForm.category || !editForm.subcategory}
              className=""
            >
              {updatingId ? 'Updating...' : 'Update Document'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tags Management Dialog */}
      <Dialog open={tagDialogOpen} onOpenChange={setTagDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TagIcon className="h-5 w-5" />
              Manage Document Tags
            </DialogTitle>
            <DialogDescription>
              Assign or remove tags from this document for better organization.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Document Info Section */}
            {tagEditingDocument && (
              <div className="grid gap-4 rounded-lg bg-muted/50 p-4">
                <h4 className="text-sm font-medium">Document Information</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Title:</span>
                    <span className="ml-2 font-medium">{tagEditingDocument.title}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Category:</span>
                    <span className="ml-2 font-medium">{tagEditingDocument.category}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Type:</span>
                    <span className="ml-2 font-medium">{tagEditingDocument.type?.toUpperCase()}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Current Tags:</span>
                    <span className="ml-2 font-medium">{tagEditingDocument.tag_count || 0}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Tag Selection */}
            {tagsLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : (
              <TagSelector
                availableTags={availableTags}
                selectedTags={selectedTags}
                onTagsChange={(uiTags: unknown[]) => {
                  const mapped: Tag[] = uiTags.map((t: unknown) => {
                    const tag = t as Record<string, unknown>
                    return {
                      id: tag.id as string,
                      name: tag.name as string,
                      color: (tag.color as string) || '#1238c4',
                      tag_type: (tag.tag_type as 'user' | 'conversation' | 'system' | 'document' | 'property')
                    }
                  })
                  setSelectedTags(mapped)
                }}
                placeholder="Select tags for this document..."
                allowCreate={true}
                onCreateTag={handleCreateTag}
                size="md"
              />
            )}
          </div>
          <div className="flex justify-end space-x-2 border-t pt-4">
            <Button variant="outline" onClick={handleCancelTags}>
              Cancel
            </Button>
            <Button 
              onClick={async () => {
                await handleTagsSubmit()
                if (!updatingTags) {
                  toast.success('Document tags updated')
                }
              }} 
              disabled={updatingTags || tagsLoading}
              className=""
            >
              {updatingTags ? 'Updating Tags...' : 'Update Tags'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
